import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
  Card,
  CardBody,
  CardFooter,
} from "reactstrap";
import { toast, Slide } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createReview, updateReview } from "../../redux/actions/reviews";
import SpaceService from "../../redux/services/space.service";
import Select from "react-select";

toast.configure();

const AddReviewForm = (props) => {
  const review = props?.review ?? {};
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [currentReviewInfo, setCurrentReviewInfo] = useState({});
  const [spaces, setSpaces] = useState([]);
  const [loadingSpaces, setLoadingSpaces] = useState(false);

  // Errors
  const [spaceErr, setSpaceErr] = useState("");
  const [userNameFromErr, setUserNameFromErr] = useState("");
  const [ratingErr, setRatingErr] = useState("");
  const [reviewErr, setReviewErr] = useState("");

  // Populate form when editing
  if (JSON.stringify(review) !== "{}" && !currentReviewInfo.id) {
    setCurrentReviewInfo(review);
  }

  // Fetch spaces for the selector
  useEffect(() => {
    setLoadingSpaces(true);
    SpaceService.getAll("", "", 1, 1000, true)
      .then((res) => {
        setSpaces(res.data.listSpaces?.rows || []);
      })
      .catch((err) => {
        console.error("Failed to load spaces:", err);
        toast("Failed to load spaces", {
          transition: Slide,
          closeButton: true,
          autoClose: 3000,
          position: "top-right",
          type: "error",
        });
      })
      .finally(() => setLoadingSpaces(false));
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setCurrentReviewInfo({ ...currentReviewInfo, [name]: value });
  };

  const handleSpaceChange = (selectedOption) => {
    setCurrentReviewInfo({
      ...currentReviewInfo,
      spaceId: selectedOption ? selectedOption.value : null,
    });
  };

  const spaceOptions = spaces.map((space) => ({
    value: space.id,
    label: `${space.title} (ID: ${space.id})`,
  }));


  const validate = () => {
    let errorCount = 0;

    if (!currentReviewInfo.spaceId) {
      setSpaceErr("Please select a space.");
      errorCount++;
    } else {
      setSpaceErr("");
    }

    if (!currentReviewInfo.userNameFrom || currentReviewInfo.userNameFrom.trim().length < 2) {
      setUserNameFromErr("Please enter at least 2 characters.");
      errorCount++;
    } else {
      setUserNameFromErr("");
    }

    const ratingVal = Number(currentReviewInfo.rating);
    if (!currentReviewInfo.rating || isNaN(ratingVal) || ratingVal < 1 || ratingVal > 5) {
      setRatingErr("Please enter a rating between 1 and 5.");
      errorCount++;
    } else {
      setRatingErr("");
    }

    return errorCount === 0;
  };

  const submitHandler = (event) => {
    event.preventDefault();
    if (!validate()) return;

    const payload = {
      spaceId: currentReviewInfo.spaceId,
      userNameFrom: currentReviewInfo.userNameFrom,
      rating: currentReviewInfo.rating,
      review: currentReviewInfo.review || "",
    };

    if (currentReviewInfo.createdAt) {
      payload.createdAt = currentReviewInfo.createdAt;
    }

    if (JSON.stringify(review) !== "{}") {
      // Edit mode
      dispatch(updateReview(currentReviewInfo.id, payload))
        .then(() => {
          toast("Review updated successfully!", {
            transition: Slide,
            closeButton: true,
            autoClose: 3000,
            position: "top-right",
            type: "success",
          });
        })
        .catch((error) => {
          toast(error?.response?.data?.message || "Failed to update review", {
            transition: Slide,
            closeButton: true,
            autoClose: 3000,
            position: "top-right",
            type: "error",
          });
        });
    } else {
      // Create mode
      dispatch(createReview(payload))
        .then((response) => {
          toast("Review created successfully!", {
            transition: Slide,
            closeButton: true,
            autoClose: 1000,
            position: "top-right",
            type: "success",
          });
          navigate(`/review/${response.reviewInfo.id}`);
        })
        .catch((error) => {
          toast(error?.response?.data?.message || "Failed to create review", {
            transition: Slide,
            closeButton: true,
            autoClose: 3000,
            position: "top-right",
            type: "error",
          });
        });
    }
  };

  return (
    <>
      <Row>
        <Col md="12">
          <Card className="main-card mb-3">
            <Form onSubmit={submitHandler}>
              <CardBody>

                {/* Space Selector */}
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label for="spaceId">Space <span className="text-danger">*</span></Label>
                      <Select
                        id="spaceId"
                        name="spaceId"
                        options={spaceOptions}
                        value={spaceOptions.find(opt => opt.value === currentReviewInfo.spaceId) || null}
                        onChange={handleSpaceChange}
                        isLoading={loadingSpaces}
                        isDisabled={loadingSpaces}
                        placeholder="Select a space..."
                        isClearable
                        className={spaceErr ? "is-invalid" : ""}
                        styles={{
                          control: (base) => ({
                            ...base,
                            borderColor: spaceErr ? '#dc3545' : base.borderColor,
                            '&:hover': {
                              borderColor: spaceErr ? '#dc3545' : base.borderColor,
                            }
                          })
                        }}
                      />
                      {spaceErr && <div className="invalid-feedback d-block">{spaceErr}</div>}
                    </FormGroup>
                  </Col>
                </Row>

                {/* Reviewer Name */}
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label for="userNameFrom">Reviewer Name <span className="text-danger">*</span></Label>
                      <Input
                        invalid={userNameFromErr !== ""}
                        type="text"
                        name="userNameFrom"
                        id="userNameFrom"
                        placeholder="Enter reviewer name..."
                        value={currentReviewInfo.userNameFrom || ""}
                        onChange={handleInputChange}
                      />
                      {userNameFromErr && <FormFeedback>{userNameFromErr}</FormFeedback>}
                    </FormGroup>
                  </Col>
                </Row>

                {/* Rating */}
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label for="rating">Rating (1–5) <span className="text-danger">*</span></Label>
                      <Input
                        invalid={ratingErr !== ""}
                        type="number"
                        name="rating"
                        id="rating"
                        placeholder="Enter rating (1 to 5)"
                        min="1"
                        max="5"
                        step="0.5"
                        value={currentReviewInfo.rating || ""}
                        onChange={handleInputChange}
                      />
                      {ratingErr && <FormFeedback>{ratingErr}</FormFeedback>}
                    </FormGroup>
                  </Col>
                </Row>

                {/* Review Text */}
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label for="review">Review</Label>
                      <Input
                        invalid={reviewErr !== ""}
                        type="textarea"
                        name="review"
                        id="review"
                        rows="4"
                        placeholder="Write review text here..."
                        value={currentReviewInfo.review || ""}
                        onChange={handleInputChange}
                      />
                      {reviewErr && <FormFeedback>{reviewErr}</FormFeedback>}
                    </FormGroup>
                  </Col>
                </Row>

                {/* Review Date */}
                <Row>
                  <Col md="12">
                    <FormGroup>
                      <Label for="createdAt">Review Date (Optional)</Label>
                      <Input
                        type="date"
                        name="createdAt"
                        id="createdAt"
                        value={currentReviewInfo.createdAt ? new Date(currentReviewInfo.createdAt).toISOString().split('T')[0] : ""}
                        onChange={handleInputChange}
                      />
                    </FormGroup>
                  </Col>
                </Row>

              </CardBody>
              <CardFooter className="d-block">
                <Button
                  className="me-2"
                  color="link"
                  type="button"
                  onClick={() => navigate("/reviews/list")}
                >
                  Cancel
                </Button>
                <Button size="lg" color="primary" type="submit">
                  {JSON.stringify(review) !== "{}" ? "Update" : "Save"}
                </Button>
              </CardFooter>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AddReviewForm;
